package com.gloopgo.game.engine

import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.sqrt

/**
 * 2D Vector with standard vector arithmetic and helper functions for game physics.
 */
data class Vector2D(val x: Float = 0f, val y: Float = 0f) {

    operator fun plus(other: Vector2D): Vector2D = Vector2D(x + other.x, y + other.y)

    operator fun minus(other: Vector2D): Vector2D = Vector2D(x - other.x, y - other.y)

    operator fun times(scalar: Float): Vector2D = Vector2D(x * scalar, y * scalar)

    operator fun div(scalar: Float): Vector2D = if (scalar != 0f) Vector2D(x / scalar, y / scalar) else ZERO

    operator fun unaryMinus(): Vector2D = Vector2D(-x, -y)

    fun lengthSquared(): Float = x * x + y * y

    fun length(): Float = sqrt(lengthSquared())

    fun magnitude(): Float = length()

    fun magnitudeSquared(): Float = lengthSquared()

    fun normalized(): Vector2D {
        val len = length()
        return if (len > 0.0001f) this / len else ZERO
    }

    fun distanceSquaredTo(other: Vector2D): Float {
        val dx = x - other.x
        val dy = y - other.y
        return dx * dx + dy * dy
    }

    fun distanceTo(other: Vector2D): Float = sqrt(distanceSquaredTo(other))

    fun angle(): Float = atan2(y, x)

    fun lerp(target: Vector2D, t: Float): Vector2D {
        val clampedT = t.coerceIn(0f, 1f)
        return Vector2D(
            x = x + (target.x - x) * clampedT,
            y = y + (target.y - y) * clampedT
        )
    }

    companion object {
        val ZERO = Vector2D(0f, 0f)

        fun fromAngle(angleRad: Float, magnitude: Float = 1f): Vector2D {
            return Vector2D(cos(angleRad) * magnitude, sin(angleRad) * magnitude)
        }
    }
}
